import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, formatError, inr } from "@/lib/api";
import {
  Package,
  MapPin,
  Heart,
  User,
  LogOut,
  Ticket,
  Save,
  Plus,
  Trash2,
  Gift,
  Download,
  RotateCcw,
  Star,
  X,
  MessageCircle,
  Share2,
  RefreshCw,
  Copy,
  Link2,
} from "lucide-react";
import { toast } from "sonner";
import { LogoutConfirmDialog } from "@/components/LogoutConfirmDialog";
import { FoodSymbol } from "@/components/FoodSymbol";
import { formatIndianPhone, PhoneNumberInput } from "@/components/PhoneNumberInput";
import { OrderFeedbackForm } from "@/components/OrderFeedbackForm";
import { EiraPointsCountdown } from "@/components/EiraPointsCountdown";
import { CustomerSupportTickets } from "@/components/account/CustomerSupportTickets";
import { ReturnRequestDialog } from "@/components/ReturnRequestDialog";
import { AccountDeletionRequest } from "@/components/account/AccountDeletionRequest";

const AddressBlock = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ full_name: "", mobile: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
  const load = () => api.get("/me/addresses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/addresses", { ...f, mobile: formatIndianPhone(f.mobile) });
      toast.success("Address saved");
      setF({ full_name: "", mobile: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this address?")) return;
    try { await api.delete(`/me/addresses/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl p-6" data-testid="addresses-block">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl">Saved Addresses</h2>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="text-sm bg-[#C86D42] hover:bg-[#AE5931] text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-1" data-testid="add-address-btn">
            <Plus size={14} /> Add address
          </button>
        )}
      </div>
      {items.length === 0 && !showForm && (
        <div className="text-center text-[#64748B] text-sm py-6">No saved addresses yet. Add one to speed up checkout.</div>
      )}
      <div className="space-y-3 mb-4">
        {items.map((a) => (
          <div key={a.id} className="border border-[#E2DCD2] rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`address-${a.id}`}>
            <div>
              <div className="font-semibold text-sm">
                {a.full_name} · <span className="text-[#64748B]">{formatIndianPhone(a.mobile)}</span>
              </div>
              <div className="text-sm text-[#64748B] mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}, {a.country}</div>
            </div>
            <button onClick={() => del(a.id)} className="text-red-500 p-2 hover:bg-red-50 rounded" data-testid={`delete-address-${a.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {showForm && (
        <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 border-t border-[#E2DCD2] pt-4">
          <input required placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className="border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="addr-name" />
          <PhoneNumberInput
            value={f.mobile}
            onChange={(value) => setF({ ...f, mobile: value })}
            required
            testId="addr-mobile"
          />
          <input required placeholder="House / Flat / Street" value={f.line1} onChange={(e) => setF({ ...f, line1: e.target.value })} className="sm:col-span-2 border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="addr-line1" />
          <input placeholder="Area / Landmark (optional)" value={f.line2} onChange={(e) => setF({ ...f, line2: e.target.value })} className="sm:col-span-2 border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          <input required placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="addr-city" />
          <input required placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="addr-state" />
          <input required placeholder="Pincode" value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} className="border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="addr-pincode" />
          <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm">
            <option>India</option><option>Nepal</option><option>Bangladesh</option><option>Sri Lanka</option>
          </select>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-[#64748B] px-4 py-2">Cancel</button>
            <button type="submit" className="bg-[#2D5A47] hover:bg-[#214435] text-white px-4 py-2 rounded-lg text-sm font-semibold" data-testid="save-address-btn">Save address</button>
          </div>
        </form>
      )}
    </div>
  );
};

const GSTDetailsBlock = () => {
  const [details, setDetails] = useState({
    company_name: "",
    gstin: "",
    company_address: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/gst-details").then((response) => setDetails(response.data)).catch(() => {});
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/me/gst-details", details);
      setDetails({
        company_name: data.company_name || "",
        gstin: data.gstin || "",
        company_address: data.company_address || "",
      });
      toast.success("GST details saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl p-6" data-testid="account-gst-details">
      <h2 className="font-serif text-2xl">GST Details</h2>
      <p className="mt-1 text-sm text-[#64748B]">
        Optional. Add these details only when you need them on your invoices.
      </p>
      <form onSubmit={save} className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-[#64748B]">Company Name</label>
          <input
            value={details.company_name}
            onChange={(event) => setDetails({ ...details, company_name: event.target.value })}
            className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2.5"
            data-testid="account-gst-company-name"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-[#64748B]">GSTIN</label>
          <input
            value={details.gstin}
            onChange={(event) => setDetails({ ...details, gstin: event.target.value.toUpperCase() })}
            className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2.5 font-mono"
            data-testid="account-gst-gstin"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-[#64748B]">Company Address</label>
          <textarea
            rows={3}
            value={details.company_address}
            onChange={(event) => setDetails({ ...details, company_address: event.target.value })}
            className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2.5"
            data-testid="account-gst-company-address"
          />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2D5A47] text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="account-gst-save"
          >
            {saving ? "Saving…" : "Save GST Details"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ProfileTab = ({ user }) => {
  const { updateCurrentUser } = useAuth();
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  useEffect(() => {
    if (!resendSeconds) return undefined;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);
  const [name, setName] = useState(user.name || "");
  const [mobile, setMobile] = useState(user.mobile || "");
  const verifyMobile = async () => {
    const digits = String(mobile || "").replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) { toast.error("Enter a valid mobile number"); return; }
    setPhoneVerifying(true);
    try {
      if (!phoneOtpSent) { await api.post("/checkout/send-phone-otp", { mobile: digits }); setPhoneOtpSent(true); setResendSeconds(60); toast.success("OTP sent on WhatsApp"); return; }
      const response = await api.post("/checkout/verify-phone", { submitted_phone: digits, otp: phoneOtp });
      updateCurrentUser(response.data); setPhoneOtp(""); setPhoneOtpSent(false); toast.success("Mobile number verified");
    } catch (error) { toast.error(formatError(error)); } finally { setPhoneVerifying(false); }
  };
  const [birthday, setBirthday] = useState(user.birthday || "");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    const normalizedMobile = formatIndianPhone(mobile);
    if (mobile && !/^\+91 \d{10}$/.test(normalizedMobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setSaving(true);
    try {
      await api.put("/me/profile", { name: name.trim(), mobile: normalizedMobile || null, birthday });
      toast.success("Profile updated");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-6" data-testid="profile-panel">
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6">
        <h2 className="font-serif text-2xl mb-4">Profile</h2>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" data-testid="profile-name" />
          </div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Email</label>
            <input value={user.email} disabled className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2.5 bg-[#F4F1EA] text-[#64748B]" data-testid="profile-email" />
            <div className="text-[10px] text-[#8C6D46] mt-1">Email cannot be changed — contact support to update.</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[#64748B] block mb-1">Mobile number</label>
            <PhoneNumberInput
              value={mobile}
              onChange={setMobile}
              testId="profile-mobile"
              className="w-full"
              verified={user.phone_verified && String(user.mobile || "").replace(/\D/g, "").slice(-10) === String(mobile || "").replace(/\D/g, "").slice(-10)}
              verifying={phoneVerifying}
              onVerify={verifyMobile}
              verifyLabel={phoneOtpSent ? "Verify OTP" : "Send OTP"}
            />
            {phoneOtpSent && <div className="mt-3"><input value={phoneOtp} onChange={(event) => setPhoneOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="Enter 6-digit WhatsApp OTP" className="w-full rounded-lg border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="profile-whatsapp-otp" /><p className="mt-2 text-xs font-medium text-amber-700" data-testid="profile-whatsapp-template-status">Waiting for WhatsApp template approval. OTP delivery begins after approval.</p><p className="mt-1 text-xs text-[#64748B]" data-testid="profile-otp-resend-countdown">{resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : "You can resend OTP now"}</p></div>}
            <div className="text-[10px] text-[#8C6D46] mt-1">We'll only text you about your orders — nothing else.</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[#64748B] block mb-1">Birthday</label>
            <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2.5" data-testid="profile-birthday" />
            <div className="text-[10px] text-[#8C6D46] mt-1">Add your birthday to receive your annual Eira Points bonus.</div>
          </div>
          <div className="sm:col-span-2 flex items-center justify-end pt-2">
            <button disabled={saving} type="submit" className="bg-[#2D5A47] hover:bg-[#214435] text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50" data-testid="profile-save">
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
      <GSTDetailsBlock />
      <AddressBlock />
      <AccountDeletionRequest />
    </div>
  );
};

const EiraPointsTab = () => {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    api.get("/me/eira-points").then((response) => setPoints(response.data));
  }, []);

  if (!points) {
    return <div data-testid="eira-points-loading">Loading Eira Points…</div>;
  }

  return (
    <div className="space-y-6" data-testid="eira-points-panel">
      <div className="border border-[#E2DCD2] bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8C6D46]">Eira Points</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-serif text-5xl font-semibold text-[#2D5A47]" data-testid="eira-points-balance">
              {points.available_points}
            </div>
            <p className="mt-1 text-sm text-[#64748B]">1 Eira Point = ₹1 at checkout</p>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold" data-testid="eira-points-pending">
              {points.pending_points} pending
            </div>
            <EiraPointsCountdown
              nextAvailableAt={points.next_available_at}
              nextAvailablePoints={points.next_available_points}
              pendingPoints={points.pending_points}
              className="mt-2 max-w-64"
            />
          </div>
        </div>
        <div className="mt-5 border-t border-[#E2DCD2] pt-4" data-testid="eira-loyalty-tier">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8C6D46]">
            Your loyalty tier
          </p>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-serif text-2xl text-[#1E2A24]">
                {points.loyalty_tier?.current_tier?.name || "Member"}
              </p>
              <p className="mt-1 text-sm text-[#64748B]">
                Up to {points.loyalty_tier?.current_tier?.redemption_percent ?? 100}% of an
                eligible order can be paid with points.
              </p>
            </div>
            {points.loyalty_tier?.next_tier && (
              <p className="text-sm font-semibold text-[#2D5A47]" data-testid="eira-tier-progress">
                {points.loyalty_tier.points_to_next_tier} points to
                {` ${points.loyalty_tier.next_tier.name}`}
              </p>
            )}
          </div>
          {points.loyalty_tier?.current_tier?.perks?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="eira-tier-perks">
              {points.loyalty_tier.current_tier.perks.map((perk) => (
                <span key={perk} className="bg-[#EBF2EE] px-2 py-1 text-xs text-[#2D5A47]">
                  {perk}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="border border-[#E2DCD2] bg-white p-6">
        <h2 className="font-serif text-2xl">Points activity</h2>
        <div className="mt-4 space-y-3">
          {points.history.length === 0 ? (
            <p className="text-sm text-[#64748B]">Your rewards and redemptions will appear here.</p>
          ) : points.history.map((entry) => {
            const isRedemption = entry.type === "redemption";
            const orderReference = entry.order_id?.slice(0, 8).toUpperCase() || "—";
            return (
              <div
                key={entry.id}
                className="flex items-center justify-between border-b border-[#E2DCD2] pb-3 text-sm"
                data-testid={`eira-points-entry-${entry.id}`}
              >
                <div>
                  <div className="font-semibold">
                    {isRedemption ? "Order redemption" : "Product review reward"}
                  </div>
                  <div className="mt-1 text-xs text-[#64748B]">
                    {isRedemption ? "Checkout discount" : entry.product_name || "Product"}
                    {" · "}Order {orderReference}
                  </div>
                  {isRedemption && Number.isFinite(Number(entry.balance_after)) && (
                    <div className="mt-1 text-xs text-[#64748B]">
                      Remaining balance: {entry.balance_after} Eira Points
                    </div>
                  )}
                </div>
                <div className={`text-right font-semibold ${
                  isRedemption ? "text-red-700" : "text-[#2D5A47]"
                }`}>
                  {isRedemption ? "−" : "+"}{Math.abs(Number(entry.points) || 0)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Account = () => {
  const { user, logout } = useAuth();
  const { addMany } = useCart();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistShareUrl, setWishlistShareUrl] = useState("");
  const [wishlistShareActive, setWishlistShareActive] = useState(false);
  const [wishlistSharing, setWishlistSharing] = useState(false);
  const [reorderingId, setReorderingId] = useState(null);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancellationChoice, setCancellationChoice] = useState("");
  const [customCancellationReason, setCustomCancellationReason] = useState("");
  const [cancellationBusy, setCancellationBusy] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [orderFeedback, setOrderFeedback] = useState(null);
  const [returningOrder, setReturningOrder] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/me/orders").then((r) => setOrders(r.data));
    api.get("/me/addresses").then((r) => setAddresses(r.data));
    api.get("/me/wishlist").then((r) => setWishlist(r.data));
    api.get("/me/wishlist/share")
      .then((response) => setWishlistShareActive(response.data.active))
      .catch(() => setWishlistShareActive(false));
  }, [user]);

  if (!user) return <div className="max-w-md mx-auto py-20 text-center">Please <Link to="/login" className="text-[#C86D42]">login</Link>.</div>;

  const menu = [
    { k: "profile", t: "Profile", i: User },
    { k: "orders", t: "My Orders", i: Package },
    { k: "wishlist", t: "Wishlist", i: Heart },
    { k: "coupons", t: "Coupons", i: Ticket },
    { k: "points", t: "Eira Points", i: Gift },
    { k: "support-tickets", t: "Support", i: MessageCircle },
  ];

  const createWishlistShare = async () => {
    setWishlistSharing(true);
    try {
      const response = await api.post("/me/wishlist/share");
      setWishlistShareUrl(response.data.share_url);
      setWishlistShareActive(true);
      try {
        await navigator.clipboard?.writeText(response.data.share_url);
      } catch {
        // The link remains visible when a browser blocks automatic clipboard access.
      }
      toast.success("Wishlist link is ready to share");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create a wishlist link");
    } finally {
      setWishlistSharing(false);
    }
  };

  const copyWishlistShare = async () => {
    try {
      await navigator.clipboard.writeText(wishlistShareUrl);
      toast.success("Wishlist link copied");
    } catch {
      toast.error("Copy the link from the field below");
    }
  };

  const revokeWishlistShare = async () => {
    try {
      await api.delete("/me/wishlist/share");
      setWishlistShareUrl("");
      setWishlistShareActive(false);
      toast.success("Wishlist link revoked");
    } catch {
      toast.error("Could not revoke the wishlist link");
    }
  };

  const downloadInvoice = async (order) => {
    try {
      const response = await api.get(`/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const anchor = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const filename = disposition.match(/filename="?([^";]+)"?/)?.[1];
      anchor.href = url;
      anchor.download = filename || `Eira-Products-Invoice-${order.id.slice(0, 8).toUpperCase()}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Invoice could not be downloaded.");
    }
  };

  const reorder = async (order) => {
    setReorderingId(order.id);
    try {
      const { data } = await api.post(`/orders/${order.id}/reorder`);
      if (data.items.length > 0) {
        addMany(data.items);
      }
      if (data.unavailable_items.length > 0) {
        toast.warning("Some items were unavailable and were not added to cart");
      } else if (data.items.length > 0) {
        toast.success("Items added to cart");
      }
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setReorderingId(null);
    }
  };
  const closeCancellation = () => {
    if (cancellationBusy) return;
    setCancellingOrder(null);
    setCancellationChoice("");
    setCustomCancellationReason("");
  };
  const cancellationReason = customCancellationReason.trim() || (
    cancellationChoice === "Other" ? "" : cancellationChoice
  );
  const confirmCancellation = async () => {
    if (!cancellingOrder || !cancellationReason) {
      toast.error("Please select or enter a cancellation reason");
      return;
    }
    setCancellationBusy(true);
    try {
      const response = await api.post(`/orders/${cancellingOrder.id}/cancel`, {
        reason: cancellationReason,
      });
      setOrders((current) => current.map((order) => (
        order.id === cancellingOrder.id ? response.data.order : order
      )));
      toast.success(
        response.data.refund?.status === "pending"
          ? "Order cancelled. Your refund is pending."
          : "Order cancelled successfully.",
      );
      setCancellingOrder(null);
      setCancellationChoice("");
      setCustomCancellationReason("");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setCancellationBusy(false);
    }
  };
  const orderStatusLabel = (status) => (status === "confirmed" ? "Order Placed" : status);
  const confirmLogout = async () => {
    setLogoutBusy(true);
    try {
      await logout();
    } finally {
      setLogoutBusy(false);
      setLogoutOpen(false);
    }
  };
  const openOrderFeedback = async (order) => {
    try {
      const response = await api.get(`/orders/${order.id}/feedback-order-card`);
      if (!response.data.eligible) {
        toast.info("Feedback has already been submitted for this order.");
        setOrders((current) => current.map((item) => (
          item.id === order.id
            ? { ...item, product_feedback_request_status: "completed" }
            : item
        )));
        return;
      }
      setOrderFeedback({ order, feedback: response.data });
    } catch (error) {
      toast.error(formatError(error));
    }
  };
  const applyReturnRequest = (returnRequest) => {
    setOrders((current) => current.map((order) => (
      order.id === returningOrder?.id
        ? { ...order, return_request: returnRequest }
        : order
    )));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="account-page">
      <h1 className="font-serif text-4xl font-semibold mb-8">Hello, {user.name}</h1>
      <div className="grid lg:grid-cols-4 gap-8">
        <aside className="space-y-1 min-w-0">
          {menu.map((m) => (
            <button key={m.k} onClick={() => setParams({ tab: m.k })} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${tab === m.k ? "bg-[#2D5A47] text-white" : "hover:bg-[#F4F1EA]"}`} data-testid={`acc-tab-${m.k}`}>
              <m.i size={16} /> {m.t}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setLogoutOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium hover:bg-[#F4F1EA] text-[#C86D42]"
            data-testid="account-logout-button"
          >
            <LogOut size={16} /> Logout
          </button>
        </aside>
        <div className="lg:col-span-3 min-w-0">
          {tab === "profile" && <ProfileTab user={user} />}
          {tab === "orders" && (
            <div className="space-y-4">
              {orders.length === 0 ? <div className="bg-white border border-[#E2DCD2] rounded-xl p-8 text-center text-[#64748B]">No orders yet.</div> :
                orders.map((o) => (
                  <div
                    key={o.id}
                    className="overflow-hidden border border-[#E2DCD2] bg-white shadow-[0_8px_24px_rgba(45,90,71,0.05)]"
                    data-testid={`order-${o.id}`}
                  >
                    <div
                      className="flex flex-col gap-4 bg-[#FDFBF7] px-5 py-4 sm:flex-row sm:items-start sm:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8C6D46]">
                          Order #{o.id.slice(0, 8)}
                        </div>
                        <div className="mt-1 text-sm text-[#64748B]">
                          {new Date(o.created_at).toLocaleString("en-IN")}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-4 sm:block sm:text-right">
                        <div className="text-xs px-2.5 py-1 rounded-full bg-[#EBF2EE] text-[#2D5A47] inline-block capitalize font-semibold">
                          {orderStatusLabel(o.order_status)}
                        </div>
                        <div className="font-serif text-xl font-semibold text-[#2D5A47] sm:mt-2">
                          {inr(o.totals.total)}
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-[#F0ECE5] px-5">
                      {o.items.map((i, k) => {
                        const itemFeedback = (o.submitted_feedback || []).find((entry) => (
                          entry.product_id === i.product_id
                          && (entry.variant_label || "") === (i.variant_label || "")
                        ));
                        return (
                          <div key={k} className="flex items-center gap-3 py-3">
                            <img
                              src={i.image}
                              alt=""
                              className="h-11 w-11 flex-none rounded-md object-cover bg-[#F4F1EA]"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-1 text-sm font-semibold text-[#1E2A24]">
                                <FoodSymbol
                                  value={i.food_symbol}
                                  className="mt-0.5"
                                  testId={`account-order-food-symbol-${o.id}-${k}`}
                                />
                                <span className="truncate">{i.name}</span>
                              </div>
                              <div className="mt-0.5 text-xs text-[#64748B]">
                                Quantity {i.quantity}
                              </div>
                              {itemFeedback ? (
                                <div
                                  className="mt-2 border-l-2 border-[#C86D42] pl-2"
                                  data-testid={`order-item-feedback-${o.id}-${k}`}
                                >
                                  <div
                                    className="flex items-center gap-0.5 text-[#C88A2D]"
                                    aria-label={`${itemFeedback.rating} out of 5 stars`}
                                    data-testid={`order-item-feedback-rating-${o.id}-${k}`}
                                  >
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        size={13}
                                        fill={star <= itemFeedback.rating ? "currentColor" : "none"}
                                        className={
                                          star <= itemFeedback.rating ? "" : "text-[#D9C3A5]"
                                        }
                                      />
                                    ))}
                                  </div>
                                  {itemFeedback.comment ? (
                                    <p
                                      className="mt-1 text-xs leading-5 text-[#475569]"
                                      data-testid={`order-item-feedback-comment-${o.id}-${k}`}
                                    >
                                      {itemFeedback.comment}
                                    </p>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            <div className="text-sm font-semibold text-[#2D5A47]">
                              {inr(i.price * i.quantity)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className="flex flex-col gap-4 border-t border-[#E2DCD2] px-5 py-4 lg:flex-row lg:items-center lg:justify-between"
                    >
                      <span className="text-xs text-[#64748B]">
                        {o.tracking?.awb
                          ? <>
                              AWB <span className="font-mono font-bold">{o.tracking.awb}</span>
                              {" · "}{o.tracking.courier}
                            </>
                          : "Awaiting shipment"}
                      </span>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {o.cancellation_window?.eligible && (
                          <button
                            type="button"
                            onClick={() => setCancellingOrder(o)}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:underline"
                            data-testid={`cancel-order-${o.id}`}
                          >
                            Cancel Order
                          </button>
                        )}
                        {o.order_status === "delivered" && o.product_feedback_request_status !== "completed" && (
                          <button
                            type="button"
                            onClick={() => openOrderFeedback(o)}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-[#C86D42] hover:underline"
                            data-testid={`rate-order-${o.id}`}
                          >
                            <Star size={14} /> Rate your order
                          </button>
                        )}
                        {o.order_status === "delivered" && !o.return_request && (
                          <button
                            type="button"
                            onClick={() => setReturningOrder(o)}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-[#A63A2B] hover:underline"
                            data-testid={`request-return-${o.id}`}
                          >
                            <RotateCcw size={14} /> Request return
                          </button>
                        )}
                        <button
                          onClick={() => reorder(o)}
                          disabled={reorderingId === o.id}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-[#2D5A47] hover:underline disabled:opacity-50"
                          data-testid={`reorder-order-${o.id}`}
                        >
                          <RotateCcw size={14} className={reorderingId === o.id ? "animate-spin" : ""} />
                          {reorderingId === o.id ? "Adding…" : "Reorder"}
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadInvoice(o)}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-[#2D5A47] hover:underline"
                          data-testid={`download-invoice-${o.id}`}
                        >
                          <Download size={14} /> Invoice
                        </button>
                        <Link
                          to={`/track/${o.id}`}
                          className="text-sm text-[#C86D42] font-semibold hover:underline"
                          data-testid={`track-btn-${o.id}`}
                        >
                          Track order →
                        </Link>
                      </div>
                    </div>
                    {o.return_request && (
                      <div
                        className="border-t border-[#E2DCD2] bg-[#FDFBF7] px-5 py-3 text-sm"
                        data-testid={`customer-return-status-${o.id}`}
                      >
                        <span className="font-semibold text-[#1E2A24]">
                          Return: {String(o.return_request.status || "requested").replace(/_/g, " ")}
                        </span>
                        {o.return_request.reverse_pickup && (
                          <span className="ml-2 text-[#2D5A47]">
                            · Pickup {String(
                              o.return_request.reverse_pickup.status
                              || o.return_request.reverse_pickup.state,
                            ).replace(/_/g, " ")}
                          </span>
                        )}
                        {o.return_request.reverse_pickup?.awb && (
                          <span className="ml-2 font-mono text-xs text-[#64748B]">
                            AWB {o.return_request.reverse_pickup.awb}
                          </span>
                        )}
                      </div>
                    )}
                    {o.order_status === "cancelled" && (
                      <div
                        className="mt-3 border border-red-100 bg-red-50 p-3 text-xs text-[#7F1D1D]"
                        data-testid={`cancelled-order-details-${o.id}`}
                      >
                        <div className="font-semibold">Cancellation reason</div>
                        <div className="mt-1">{o.cancellation?.reason || o.cancellation_reason}</div>
                        <div className="mt-2 font-medium">
                          {o.refund?.status === "not_required"
                            ? "No refund is needed for this Cash on Delivery order."
                            : o.refund?.status === "initiated"
                              ? "Refund initiated to the original payment method."
                              : "Refund pending for the original payment method."}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              }
            </div>
          )}
          {tab === "addresses" && (
            <div className="space-y-3">
              {addresses.length === 0 ? <div className="bg-white border border-[#E2DCD2] rounded-xl p-8 text-center text-[#64748B]">No saved addresses. Add one at checkout.</div> :
                addresses.map((a) => (
                  <div key={a.id} className="bg-white border border-[#E2DCD2] rounded-xl p-5">
                    <div className="font-semibold">
                      {a.full_name} · {formatIndianPhone(a.mobile)}
                    </div>
                    <div className="text-sm text-[#64748B]">{a.line1}, {a.line2}, {a.city}, {a.state} {a.pincode}</div>
                  </div>
                ))
              }
            </div>
          )}
          {tab === "wishlist" && (
            <div>
              <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h2 className="font-serif text-2xl">Your Wishlist</h2>
                  <p className="mt-1 text-sm text-[#64748B]">Share your saved favourites with a gift giver.</p>
                </div>
                <button
                  type="button"
                  onClick={createWishlistShare}
                  disabled={wishlistSharing || !wishlist.length}
                  className={[
                    "inline-flex items-center gap-2 rounded-lg bg-[#2D5A47] px-4 py-2.5",
                    "text-sm font-semibold text-white hover:bg-[#214435]",
                    "disabled:cursor-not-allowed disabled:bg-[#CBD5CF]",
                  ].join(" ")}
                  data-testid="account-share-wishlist-button"
                >
                  {wishlistShareActive ? <RefreshCw size={16} /> : <Share2 size={16} />}
                  {wishlistSharing
                    ? "Creating link…"
                    : wishlistShareActive
                      ? "Refresh share link"
                      : "Share Wishlist"}
                </button>
              </div>
              {wishlistShareUrl && (
                <section
                  className="mb-5 border border-[#D9C3A5] bg-[#FDFBF7] p-4"
                  data-testid="account-wishlist-share-panel"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-[#1E2A24]">A link for gifting</h3>
                      <p className="mt-1 text-xs text-[#64748B]">
                        Anyone with this link can browse your wishlist.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={revokeWishlistShare}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#A63A2B]"
                      data-testid="account-revoke-wishlist-share-button"
                    >
                      <X size={16} /> Revoke link
                    </button>
                  </div>
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <div
                      className={[
                        "flex min-w-0 flex-1 items-center gap-2 border border-[#E2DCD2] bg-white",
                        "px-3 py-2 text-sm text-[#64748B]",
                      ].join(" ")}
                      data-testid="account-wishlist-share-url"
                    >
                      <Link2 size={16} className="shrink-0 text-[#2D5A47]" />
                      <span className="truncate">{wishlistShareUrl}</span>
                    </div>
                    <button
                      type="button"
                      onClick={copyWishlistShare}
                      className={[
                        "inline-flex items-center justify-center gap-2 border border-[#2D5A47] px-4",
                        "py-2 text-sm font-semibold text-[#2D5A47] hover:bg-[#EBF2EE]",
                      ].join(" ")}
                      data-testid="account-copy-wishlist-share-button"
                    >
                      <Copy size={16} /> Copy link
                    </button>
                  </div>
                </section>
              )}
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {wishlist.length === 0 ? (
                  <div className="col-span-full rounded-xl border border-[#E2DCD2] bg-white p-8 text-center text-[#64748B]">
                    Your wishlist is empty.
                  </div>
                ) : (
                  wishlist.map((product) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.slug}`}
                      className="overflow-hidden rounded-xl border border-[#E2DCD2] bg-white"
                    >
                      <img src={product.images?.[0]} alt="" className="aspect-square w-full object-cover" />
                      <div className="p-3">
                        <div className="flex items-start gap-1 text-sm font-semibold line-clamp-2">
                          <FoodSymbol
                            value={product.food_symbol}
                            className="mt-0.5"
                            testId={`wishlist-food-symbol-${product.id}`}
                          />
                          <span>{product.name}</span>
                        </div>
                        <div className="mt-1 font-bold text-[#2D5A47]">{inr(product.price)}</div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}
          {tab === "coupons" && (
            <div className="bg-white border border-[#E2DCD2] rounded-xl p-6">
              <h2 className="font-serif text-2xl mb-4">Available Coupons</h2>
              <div className="space-y-3">
                <div className="border border-dashed border-[#C86D42] rounded-lg p-4 bg-[#FEF6EF]"><span className="font-mono font-bold">WELCOME10</span> — 10% off on first order (min ₹499)</div>
                <div className="border border-dashed border-[#C86D42] rounded-lg p-4 bg-[#FEF6EF]"><span className="font-mono font-bold">FLAT100</span> — Flat ₹100 off (min ₹999)</div>
                <div className="border border-dashed border-[#C86D42] rounded-lg p-4 bg-[#FEF6EF]"><span className="font-mono font-bold">FREESHIP</span> — Free shipping on any order</div>
              </div>
            </div>
          )}
          {tab === "points" && <EiraPointsTab />}
          {tab === "support-tickets" && (
            <CustomerSupportTickets focusTicketId={params.get("ticket") || ""} />
          )}
        </div>
      </div>
      {cancellingOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E2A24]/50 p-4"
          data-testid="cancel-order-modal"
        >
          <div className="w-full max-w-md bg-white p-6 shadow-xl">
            <h2 className="font-serif text-2xl text-[#1E2A24]">Cancel order?</h2>
            <p className="mt-2 text-sm text-[#64748B]">
              Please tell us why you would like to cancel this order.
            </p>
            <label className="mt-5 block text-xs font-semibold text-[#64748B]">
              Cancellation reason
              <select
                value={cancellationChoice}
                onChange={(event) => setCancellationChoice(event.target.value)}
                className="mt-1.5 w-full border border-[#E2DCD2] px-3 py-2.5 text-sm"
                data-testid="cancel-order-reason-select"
              >
                <option value="">Select a reason</option>
                <option value="Ordered by mistake">Ordered by mistake</option>
                <option value="Changed my mind">Changed my mind</option>
                <option value="Found a better price">Found a better price</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label className="mt-4 block text-xs font-semibold text-[#64748B]">
              Or enter a reason
              <textarea
                rows={3}
                value={customCancellationReason}
                onChange={(event) => setCustomCancellationReason(event.target.value)}
                placeholder="Enter a cancellation reason"
                className="mt-1.5 w-full border border-[#E2DCD2] px-3 py-2.5 text-sm"
                data-testid="cancel-order-reason-input"
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeCancellation}
                disabled={cancellationBusy}
                className="px-4 py-2 text-sm font-semibold text-[#64748B] disabled:opacity-50"
                data-testid="cancel-order-dismiss"
              >
                Keep order
              </button>
              <button
                type="button"
                onClick={confirmCancellation}
                disabled={!cancellationReason || cancellationBusy}
                className="bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                data-testid="confirm-cancel-order"
              >
                {cancellationBusy ? "Cancelling…" : "Confirm cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
      {orderFeedback && (
        <div
          className="fixed inset-0 z-[90] flex items-end bg-[#1E2A24]/45 p-4 sm:items-center sm:justify-center"
          data-testid="order-card-feedback-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Rate your order"
        >
          <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto bg-[#FDFBF7] p-5 shadow-2xl sm:p-7">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
                  Delivered order #{orderFeedback.order.id.slice(0, 8)}
                </p>
                <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">Rate your order</h2>
                <p className="mt-2 text-sm text-[#64748B]">Share a rating and optional review for each product.</p>
              </div>
              <button
                type="button"
                onClick={() => setOrderFeedback(null)}
                className="rounded border border-[#E2DCD2] p-2 text-[#64748B] hover:border-[#C86D42] hover:text-[#C86D42]"
                aria-label="Close order feedback"
                data-testid="dismiss-order-card-feedback"
              >
                <X size={16} />
              </button>
            </div>
            <OrderFeedbackForm
              feedback={orderFeedback.feedback}
              testIdPrefix="order-card"
              onSubmitted={() => {
                setOrders((current) => current.map((item) => (
                  item.id === orderFeedback.order.id
                    ? { ...item, product_feedback_request_status: "completed" }
                    : item
                )));
                setOrderFeedback(null);
              }}
            />
          </div>
        </div>
      )}
      <LogoutConfirmDialog
        open={logoutOpen}
        busy={logoutBusy}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
        testIdPrefix="account"
      />
      {returningOrder && (
        <ReturnRequestDialog
          order={returningOrder}
          onClose={() => setReturningOrder(null)}
          onRequested={applyReturnRequest}
        />
      )}
    </div>
  );
};

export default Account;
